import { resolveKryspaceReadSource, type KryspaceReadSource } from '../config/kryspaceReadSource';
import { apiClient } from './apiClient';
import { isKryspaceRenderServiceError, mapRenderManifestHttpError } from './kryspaceRenderService';

export interface KryspaceSlugProbeResult {
  slug: string;
  reachable: boolean;
  /** When true, slug stays selectable despite probe ambiguity (timeout/network). */
  probeUncertain?: boolean;
  httpStatus?: number;
  code?: string;
  message?: string;
}

const probeCache = new Map<string, KryspaceSlugProbeResult>();

function attentionManifestUrl(slug: string, source: KryspaceReadSource): string {
  return `/kryspace/workspace/${encodeURIComponent(slug)}/attention-manifest?source=${source}`;
}

function isDefinitivelyUnreachable(httpStatus?: number, code?: string): boolean {
  if (httpStatus === 400 || httpStatus === 404) return true;
  if (code === 'NOT_FOUND' || code === 'INVALID_SLUG') return true;
  return false;
}

function orderedSources(slug: string): KryspaceReadSource[] {
  const primary = resolveKryspaceReadSource({ workspaceSlug: slug });
  // Phase 4: production must not probe workspace disk after choosing source=db.
  if (process.env.NODE_ENV === 'production') {
    return [primary];
  }
  return primary === 'db' ? ['db', 'workspace'] : ['workspace', 'db'];
}

function sourceTimeout(source: KryspaceReadSource): number {
  // Local thin-read db path can exceed 8s on cold kryspace manifest builds.
  return source === 'db' ? 25000 : 8000;
}

function mapProbeError(slug: string, err: unknown): KryspaceSlugProbeResult {
  const serviceErr = isKryspaceRenderServiceError(err) ? err : mapRenderManifestHttpError(err);
  const ax = err as { response?: { status?: number }; code?: string; message?: string };
  const httpStatus = serviceErr.httpStatus ?? ax.response?.status;
  const isTimeout =
    ax.code === 'ECONNABORTED' ||
    (typeof ax.message === 'string' && ax.message.toLowerCase().includes('timeout'));

  if (isTimeout || serviceErr.code === 'SERVICE_UNAVAILABLE') {
    return {
      slug,
      reachable: true,
      probeUncertain: true,
      httpStatus,
      code: serviceErr.code,
      message: serviceErr.message,
    };
  }

  return {
    slug,
    reachable: false,
    httpStatus,
    code: serviceErr.code,
    message: serviceErr.message,
  };
}

/** Lightweight reachability check — avoids selecting slugs that 400/404 on manifest read. */
export async function probeKryspaceWorkspaceSlug(slug: string): Promise<KryspaceSlugProbeResult> {
  const normalized = slug.trim();
  if (!normalized) {
    return { slug: normalized, reachable: false, code: 'INVALID_SLUG', message: 'Empty slug' };
  }

  const cached = probeCache.get(normalized);
  if (cached) return cached;

  let lastFailure: KryspaceSlugProbeResult | undefined;

  for (const source of orderedSources(normalized)) {
    try {
      const response = await apiClient.get(attentionManifestUrl(normalized, source), {
        timeout: sourceTimeout(source),
      });
      if (response.status >= 400) {
        lastFailure = {
          slug: normalized,
          reachable: false,
          httpStatus: response.status,
          message: `HTTP ${response.status}`,
        };
        if (isDefinitivelyUnreachable(response.status)) {
          probeCache.set(normalized, lastFailure);
          return lastFailure;
        }
        continue;
      }
      const ok: KryspaceSlugProbeResult = { slug: normalized, reachable: true };
      probeCache.set(normalized, ok);
      return ok;
    } catch (err: unknown) {
      const mapped = mapProbeError(normalized, err);
      if (mapped.probeUncertain) {
        probeCache.set(normalized, mapped);
        return mapped;
      }
      lastFailure = mapped;
      if (isDefinitivelyUnreachable(mapped.httpStatus, mapped.code)) {
        probeCache.set(normalized, mapped);
        return mapped;
      }
    }
  }

  if (lastFailure && isDefinitivelyUnreachable(lastFailure.httpStatus, lastFailure.code)) {
    probeCache.set(normalized, lastFailure);
    return lastFailure;
  }

  // Ambiguous failure (403 membership, etc.) — fail open so operator can still try.
  const uncertain: KryspaceSlugProbeResult = {
    slug: normalized,
    reachable: true,
    probeUncertain: true,
    httpStatus: lastFailure?.httpStatus,
    code: lastFailure?.code,
    message: lastFailure?.message,
  };
  probeCache.set(normalized, uncertain);
  return uncertain;
}

/** Test helper */
export function clearKryspaceSlugProbeCache(): void {
  probeCache.clear();
}
